import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send,
  Loader2,
  Sparkles,
  Bot,
  User,
  GitBranch,
  Check,
  RefreshCw,
  MessageSquare,
  History,
  X,
  Cpu,
} from 'lucide-react';
import { Node, Edge } from 'reactflow';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { cn, getErrorMessage } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface DraftNode {
  id: string;
  nodeType: string;
  displayName: string;
  config: Record<string, unknown>;
  position: { x: number; y: number };
}

interface DraftEdge {
  id: string;
  source: string;
  target: string;
}

interface DraftWorkflow {
  nodes: DraftNode[];
  edges: DraftEdge[];
}

interface ConversationListItem {
  id: string;
  title: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ConversationDetail {
  id: string;
  title: string | null;
  status: string;
  messages?: unknown;
  draft_workflow?: DraftWorkflow | null;
}

interface ChatResponsePayload {
  message: string;
  draftWorkflow?: DraftWorkflow;
  isComplete: boolean;
  model_used?: string;
  provider_used?: string;
}

interface ActiveModelInfo {
  model: string;
  provider: string;
  temperature?: number;
  maxTokens?: number;
}

interface CurrentWorkflowContext {
  name?: string;
  description?: string;
  triggerType?: string;
  isNew?: boolean;
  nodes?: Node[];
  edges?: Edge[];
}

interface AIWorkflowBuilderChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerateWorkflow: (nodes: Node[], edges: Edge[]) => void;
  currentWorkflow?: CurrentWorkflowContext;
}

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffH = Math.floor(diffMs / (60 * 60 * 1000));
  if (diffH < 1) return 'Pochi minuti fa';
  if (diffH < 24) return `${diffH}h fa`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}g fa`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function AIWorkflowBuilderChatModal({
  open,
  onOpenChange,
  onGenerateWorkflow,
  currentWorkflow,
}: AIWorkflowBuilderChatModalProps) {
  const { toast } = useToast();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [draftWorkflow, setDraftWorkflow] = useState<DraftWorkflow | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [activeModel, setActiveModel] = useState<ActiveModelInfo | null>(null);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<ConversationListItem[]>([]);

  const [confirmClose, setConfirmClose] = useState(false);
  const [pendingResumeId, setPendingResumeId] = useState<string | null>(null);
  const allowCloseRef = useRef(false);

  const userMessageCount = messages.filter((m) => m.role === 'user').length;
  const isDirty = userMessageCount > 0;

  const fetchActiveModel = useCallback(async () => {
    try {
      const res = await apiClient.get<ActiveModelInfo>('/ai/workflow-builder/active-model');
      if (res.success && res.data) {
        setActiveModel(res.data);
      }
    } catch (err) {
      console.error('[AIWorkflowBuilderChat] active-model error:', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiClient.get<ConversationListItem[]>('/ai/workflow-builder/conversations');
      if (res.success && res.data) {
        setHistory(res.data);
      }
    } catch (err) {
      console.error('[AIWorkflowBuilderChat] history error:', err);
      toast({ title: 'Errore', description: 'Impossibile caricare la cronologia', variant: 'destructive' });
    } finally {
      setHistoryLoading(false);
    }
  }, [toast]);

  const startConversation = useCallback(async () => {
    try {
      const res = await apiClient.post<{ id: string }>('/ai/workflow-builder/conversations');
      if (res.success && res.data) {
        setConversationId(res.data.id);
        setMessages([
          {
            role: 'assistant',
            content: 'Ciao, dimmi cosa vuoi automatizzare e lo costruiamo insieme.',
          },
        ]);
        setDraftWorkflow(null);
        setIsComplete(false);
      }
    } catch (error) {
      console.error('[AIWorkflowBuilderChat] Error starting conversation:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile avviare la conversazione',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const performResume = useCallback(
    async (id: string) => {
      try {
        const res = await apiClient.get<ConversationDetail>(`/ai/workflow-builder/conversations/${id}`);
        if (res.success && res.data) {
          const conv = res.data;
          const rawMessages = Array.isArray(conv.messages) ? conv.messages : [];
          const msgs: ChatMessage[] = rawMessages
            .filter((m): m is { role: 'user' | 'assistant'; content: string } => {
              if (!m || typeof m !== 'object') return false;
              const r = (m as { role?: unknown }).role;
              const c = (m as { content?: unknown }).content;
              return (r === 'user' || r === 'assistant') && typeof c === 'string';
            })
            .map((m) => ({ role: m.role, content: m.content }));
          setConversationId(id);
          setMessages(msgs);
          setDraftWorkflow(conv.draft_workflow || null);
          setIsComplete(conv.status === 'completed' || conv.status === 'complete');
          setHistoryOpen(false);
        }
      } catch (err) {
        console.error('[AIWorkflowBuilderChat] resume error:', err);
        toast({ title: 'Errore', description: 'Impossibile riprendere la conversazione', variant: 'destructive' });
      }
    },
    [toast]
  );

  const requestResume = useCallback(
    (id: string) => {
      if (id === conversationId) return;
      if (isDirty) {
        setPendingResumeId(id);
      } else {
        performResume(id);
      }
    },
    [conversationId, isDirty, performResume]
  );

  const confirmAndResume = useCallback(() => {
    const id = pendingResumeId;
    setPendingResumeId(null);
    if (id) performResume(id);
  }, [pendingResumeId, performResume]);

  useEffect(() => {
    if (open && !conversationId) {
      startConversation();
    }
    if (open) {
      fetchActiveModel();
    }
  }, [open, conversationId, startConversation, fetchActiveModel]);

  useEffect(() => {
    if (historyOpen) fetchHistory();
  }, [historyOpen, fetchHistory]);

  useEffect(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!inputValue.trim() || !conversationId || loading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const ctxPayload = currentWorkflow
        ? {
            name: currentWorkflow.name,
            description: currentWorkflow.description,
            triggerType: currentWorkflow.triggerType,
            isNew: currentWorkflow.isNew,
            nodes: (currentWorkflow.nodes || []).map((n) => ({
              id: n.id,
              type: n.type,
              position: n.position,
              data: n.data,
            })),
            edges: (currentWorkflow.edges || []).map((e) => ({
              id: e.id,
              source: e.source,
              target: e.target,
              sourceHandle: e.sourceHandle,
              targetHandle: e.targetHandle,
            })),
          }
        : undefined;

      const res = await apiClient.post<ChatResponsePayload>('/ai/workflow-builder/chat', {
        conversationId,
        message: userMessage,
        currentWorkflowContext: ctxPayload,
      });

      if (res.success && res.data) {
        setMessages((prev) => [...prev, { role: 'assistant', content: res.data!.message! }]);

        if (res.data.draftWorkflow) {
          setDraftWorkflow(res.data.draftWorkflow);
          setIsComplete(res.data.isComplete);
        }

        if (res.data.model_used && res.data.provider_used) {
          setActiveModel((prev) => ({
            model: res.data!.model_used!,
            provider: res.data!.provider_used!,
            temperature: prev?.temperature,
            maxTokens: prev?.maxTokens,
          }));
        }
      }
    } catch (error) {
      console.error('[AIWorkflowBuilderChat] Error:', error);
      toast({
        title: 'Errore',
        description: getErrorMessage(error) || 'Errore durante la comunicazione',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setConversationId(null);
    setMessages([]);
    setDraftWorkflow(null);
    setIsComplete(false);
  };

  const handleGenerateWorkflow = () => {
    if (!draftWorkflow) return;

    const nodes: Node[] = draftWorkflow.nodes.map((n, idx) => ({
      id: n.id,
      type: n.nodeType.startsWith('trigger_')
        ? 'trigger'
        : n.nodeType.includes('condition')
        ? 'flowControl'
        : 'action',
      position: n.position || { x: 400, y: 100 + idx * 150 },
      data: {
        nodeId: n.nodeType,
        label: n.displayName,
        config: n.config,
        description: '',
      },
    }));

    const edges: Edge[] = draftWorkflow.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: 'smoothstep',
      animated: true,
      style: { strokeWidth: 2, stroke: '#06b6d4' },
    }));

    onGenerateWorkflow(nodes, edges);

    toast({
      title: 'Workflow generato!',
      description: `Aggiunti ${nodes.length} nodi al canvas`,
    });

    resetConversation();
    allowCloseRef.current = true;
    onOpenChange(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const requestClose = () => {
    if (isDirty) {
      setConfirmClose(true);
    } else {
      allowCloseRef.current = true;
      resetConversation();
      onOpenChange(false);
    }
  };

  const confirmAndClose = () => {
    setConfirmClose(false);
    allowCloseRef.current = true;
    resetConversation();
    onOpenChange(false);
  };

  useEffect(() => {
    if (!open && !allowCloseRef.current && isDirty) {
      onOpenChange(true);
      setConfirmClose(true);
    }
    if (open) {
      allowCloseRef.current = false;
    }
  }, [open, isDirty, onOpenChange]);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) return;
          onOpenChange(next);
        }}
      >
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            className={cn(
              'fixed z-50 bg-background shadow-2xl duration-200',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
              'w-[96vw] max-w-[min(96vw,1440px)] h-[92vh] max-h-[92vh]',
              'rounded-lg border p-0 flex flex-col overflow-hidden',
              'focus:outline-none focus-visible:outline-none focus-visible:ring-0'
            )}
          >
            <DialogTitle className="sr-only">AI Workflow Builder</DialogTitle>
            <DialogDescription className="sr-only">
              Chat con il Workflow Builder AI per generare workflow automaticamente
            </DialogDescription>

            {/* Header */}
            <div className="flex items-center justify-between gap-3 p-4 border-b shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold leading-tight">AI Workflow Builder</h2>
                  <p className="text-xs text-muted-foreground truncate">
                    Descrivi cosa vuoi automatizzare e l'AI creerà il workflow per te
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {activeModel && (
                  <Badge
                    variant="outline"
                    className="hidden md:inline-flex items-center gap-1.5 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-[11px] font-normal"
                  >
                    <Cpu className="h-3 w-3" />
                    Powered by <span className="font-mono font-medium">{activeModel.model}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="font-medium">{activeModel.provider}</span>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setHistoryOpen((v) => !v)}
                  className="h-8 gap-1.5 text-xs"
                  title="Cronologia conversazioni (15 giorni)"
                >
                  <History className="h-3.5 w-3.5" />
                  Cronologia
                </Button>
                <button
                  type="button"
                  onClick={requestClose}
                  className="rounded-md h-8 w-8 inline-flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                  title="Chiudi"
                  aria-label="Chiudi"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-1 overflow-hidden">
              {/* History side panel */}
              {historyOpen && (
                <div className="w-72 border-r flex flex-col bg-muted/20 shrink-0">
                  <div className="p-3 border-b flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium flex items-center gap-1.5">
                        <History className="h-3.5 w-3.5" />
                        Cronologia
                      </h4>
                      <p className="text-[10px] text-muted-foreground">Conservata per 15 giorni</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={fetchHistory}
                      disabled={historyLoading}
                      title="Aggiorna"
                    >
                      {historyLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <ScrollArea className="flex-1">
                    <div className="p-2 space-y-1">
                      {historyLoading && history.length === 0 ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : history.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-6 px-3">
                          Nessuna conversazione recente
                        </p>
                      ) : (
                        history.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => requestResume(c.id)}
                            className={cn(
                              'w-full text-left px-2.5 py-2 rounded-md transition-colors',
                              'hover:bg-background border border-transparent hover:border-border',
                              conversationId === c.id && 'bg-background border-purple-200 dark:border-purple-800'
                            )}
                          >
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className="text-xs font-medium truncate">
                                {c.title || 'Conversazione senza titolo'}
                              </span>
                              {(c.status === 'completed' || c.status === 'complete') && (
                                <Badge variant="outline" className="text-[9px] h-4 px-1 border-green-200 text-green-600">
                                  ✓
                                </Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground">{formatRelativeDate(c.updated_at)}</p>
                          </button>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Chat column (~70%) */}
              <div className="flex-[7] flex flex-col border-r min-w-0">
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4 max-w-4xl mx-auto">
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.role === 'assistant' && (
                          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                            <Bot className="h-4 w-4 text-purple-500" />
                          </div>
                        )}
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        </div>
                        {msg.role === 'user' && (
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                        )}
                      </div>
                    ))}
                    {loading && (
                      <div className="flex gap-3 justify-start">
                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Bot className="h-4 w-4 text-purple-500" />
                        </div>
                        <div className="bg-muted rounded-lg p-3">
                          <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                <div className="p-4 border-t shrink-0">
                  <div className="flex gap-2 max-w-4xl mx-auto">
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Descrivi il workflow che vuoi creare..."
                      disabled={loading}
                      className="flex-1"
                    />
                    <Button onClick={sendMessage} disabled={!inputValue.trim() || loading} size="icon">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                  {activeModel && (
                    <p className="md:hidden text-[10px] text-muted-foreground text-center mt-2">
                      Powered by {activeModel.model} · {activeModel.provider}
                    </p>
                  )}
                </div>
              </div>

              {/* Preview column (~30%) */}
              <div className="flex-[3] flex flex-col bg-muted/30 min-w-0">
                <div className="p-4 border-b">
                  <h4 className="font-medium flex items-center gap-2">
                    <GitBranch className="h-4 w-4" />
                    Preview Workflow
                  </h4>
                </div>

                <ScrollArea className="flex-1 p-4">
                  {draftWorkflow && draftWorkflow.nodes.length > 0 ? (
                    <div className="space-y-3">
                      {draftWorkflow.nodes.map((node, idx) => (
                        <div key={node.id} className="p-3 bg-background rounded-lg border shadow-sm">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {idx + 1}
                            </Badge>
                            <span className="font-medium text-sm">{node.displayName}</span>
                          </div>
                          <code className="text-xs text-muted-foreground">{node.nodeType}</code>
                        </div>
                      ))}

                      {draftWorkflow.edges.length > 0 && (
                        <div className="pt-2 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Connessioni:</p>
                          {draftWorkflow.edges.map((edge) => (
                            <div key={edge.id} className="text-xs text-muted-foreground">
                              {edge.source} → {edge.target}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">I nodi appariranno qui man mano che l'AI costruisce il workflow</p>
                    </div>
                  )}
                </ScrollArea>

                <div className="p-4 border-t space-y-2">
                  <Button
                    onClick={handleGenerateWorkflow}
                    disabled={!draftWorkflow || draftWorkflow.nodes.length === 0}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Genera Workflow
                  </Button>
                  <Button onClick={resetConversation} variant="outline" className="w-full">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Nuova Conversazione
                  </Button>
                </div>
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Chiudere la conversazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Hai una conversazione in corso. Se chiudi adesso il workflow non verrà generato,
              ma la chat resta in cronologia per 15 giorni e potrai riprenderla.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAndClose}>Chiudi comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingResumeId !== null}
        onOpenChange={(o) => {
          if (!o) setPendingResumeId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Riprendere un'altra conversazione?</AlertDialogTitle>
            <AlertDialogDescription>
              La conversazione corrente verrà sostituita. La chat in corso resta salvata
              in cronologia per 15 giorni e potrai riprenderla in seguito.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAndResume}>Riprendi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
